import {
  collectionGroup,
  endBefore,
  getDocs,
  limit,
  limitToLast,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where
} from "firebase/firestore";
import { useEffect, useRef, useState } from "react";

import {
  getProcedureHistoryAsset,
  putProceduresHistory
} from "../Redux/proceduresHistory/action";
import { setErrorStatus } from "../Redux/status/action";
import { setTablePageNumber } from "../Redux/table/action";
import { exportFiles } from "../Services/api";
import { database } from "../Services/firebase";
import { dateAndTimeConverter } from "../Utils/Constants/Constants";
import {
  procedureHistoryQueryBuilder,
  procedureHistoryTableField
} from "../Utils/Constants/ProcedureHistory";
const listenerDataLimit = 50;

export function useProcedureHistory(props) {
  const procedureHistoryListener = useRef(null);
  const procedureHistoryData = useRef(null);
  const [procedureHistoryLoading, setProcedureHistoryLoading] = useState({
    pagination: false,
    search: false,
    export: false
  });
  const currentCursor = useRef({
    prev: null,
    next: null
  });
  const pageNumber = useRef(1);
  const filterQuery = useRef([]);
  const listeningFilterData = useRef(null);

  const getProcedureHistoryNextPage = async () => {
    pageNumber.current += 1;
    setTablePageNumber("PROCEDURE_HISTORY", pageNumber.current);
    currentCursor.current = await getPaginationIncrement({
      procedureHistoryData: procedureHistoryData.current,
      procedureHistoryListener: procedureHistoryListener.current,
      setProcedureHistoryLoading: setProcedureHistoryLoading,
      currentCursor: currentCursor.current,
      filterQuery: filterQuery.current,
      listeningFilterData: listeningFilterData.current,
      procedureHistoryLoading: procedureHistoryLoading,
      sort: props.sort,
      pageNumber: pageNumber.current
    });
  };

  const getProcedureHistoryPreviousPage = async () => {
    pageNumber.current -= 1;
    setTablePageNumber("PROCEDURE_HISTORY", pageNumber.current);
    currentCursor.current = await getPaginationDecrement({
      procedureHistoryData: procedureHistoryData.current,
      procedureHistoryListener: procedureHistoryListener.current,
      setProcedureHistoryLoading: setProcedureHistoryLoading,
      currentCursor: currentCursor.current,
      filterQuery: filterQuery.current,
      listeningFilterData: listeningFilterData.current,
      procedureHistoryLoading: procedureHistoryLoading,
      sort: props.sort,
      pageNumber: pageNumber.current
    });
  };

  const getHistorySummary = async () => {
    await downloadHistorySummary({
      accessToken: props.accessToken,
      procedureHistoryLoading: procedureHistoryLoading,
      filterQuery: filterQuery.current,
      setProcedureHistoryLoading: setProcedureHistoryLoading
    });
  };

  // Listener
  const subscribeToProcedureHistory = () => {
    const hasInequalityFilter =
      Array.isArray(filterQuery.current) &&
      filterQuery.current.some((operator) =>
        ["<", "<=", ">", ">=", "not-in", "!="].includes(operator)
      );

    const sortField = procedureHistoryTableField(props.sort?.title, "sort");
    const sortOrder = props.sort?.ascending ? "asc" : "desc";
    const baseQueryConstraints = [
      orderBy(sortField, sortOrder),
      limit(listenerDataLimit)
    ];

    let queryRef;

    if (hasInequalityFilter) {
      queryRef = query(
        collectionGroup(database, "procedureHistory"),
        sortField !== "createdAt" && orderBy("createdAt", "desc"),
        ...baseQueryConstraints
      );
    } else {
      queryRef = query(
        collectionGroup(database, "procedureHistory"),
        ...baseQueryConstraints
      );
    }

    filterQuery.current.forEach((value, index) => {
      if ((index + 1) % 3 === 0 && index !== 0) {
        queryRef = query(
          queryRef,
          where(
            filterQuery.current[index - 2],
            filterQuery.current[index - 1],
            value
          )
        );
      }
    });

    return onSnapshot(
      queryRef,
      (dataSnapshot) => {
        let procedureHistory = {};
        dataSnapshot.forEach((doc) => {
          procedureHistory[doc.id] = {
            ...doc.data(),
            documentId: doc.id
          };
        });

        currentCursor.current.next =
          dataSnapshot.docs[dataSnapshot.docs.length - 1];

        if (
          procedureHistoryData.current === null ||
          JSON.stringify(listeningFilterData.current) !==
            JSON.stringify(filterQuery.current)
        ) {
          listeningFilterData.current = filterQuery.current;
          procedureHistoryData.current = procedureHistory;
        } else {
          procedureHistoryData.current = procedureHistory;
        }

        putProceduresHistory(procedureHistoryData.current, filterQuery.current);
      },
      (error) => {
        console.error(error, "from ProceduresHistory listener");
        setErrorStatus(error);
      }
    );
  };

  useEffect(() => {
    const query = procedureHistoryQueryBuilder(props.filterData, props.locationData);
    filterQuery.current = query;

    if (
      typeof procedureHistoryListener.current === "function" &&
      props.isAuth &&
      JSON.stringify(listeningFilterData.current) !==
        JSON.stringify(filterQuery.current)
    ) {
      procedureHistoryData.current = null;
      listeningFilterData.current = filterQuery.current;
      procedureHistoryListener.current();
      procedureHistoryListener.current = subscribeToProcedureHistory();
      pageNumber.current = 1;
      setTablePageNumber("PROCEDURE_HISTORY", pageNumber.current);
    }
    // eslint-disable-next-line
  }, [props.filterData]);

  useEffect(() => {
    if (
      props.isAuth &&
      typeof procedureHistoryListener.current === "function"
    ) {
      procedureHistoryListener.current();
      procedureHistoryListener.current = subscribeToProcedureHistory();
      pageNumber.current = 1;
      setTablePageNumber("PROCEDURE_HISTORY", pageNumber.current);
    }
    // eslint-disable-next-line
  }, [props.isAuth, props.sort]);

  useEffect(() => {
    if (props.isAuth && procedureHistoryListener.current === null) {
      procedureHistoryListener.current = subscribeToProcedureHistory();
    } else if (
      props.isAuth === false &&
      typeof procedureHistoryListener.current === "function"
    ) {
      procedureHistoryListener.current();
      procedureHistoryListener.current = null;
    }
    // eslint-disable-next-line
  }, [props.isAuth]);

  useEffect(() => {
    if (
      props.selectedProcedureHistory?.latestUpdate?.some(
        (update) =>
          typeof update.fileSrc === "undefined" ||
          update.proof.length !== Object.values(update.fileSrc).length
      )
    ) {
      getProcedureHistoryAsset(props.selectedProcedureHistory);
    }
    // eslint-disable-next-line
  }, [props.selectedProcedureHistory]);

  return {
    getProcedureHistoryNextPage,
    getProcedureHistoryPreviousPage,
    procedureHistoryLoading,
    getHistorySummary
  };
}

const downloadHistorySummary = async (props) => {
  try {
    props.setProcedureHistoryLoading({
      ...props.procedureHistoryLoading,
      export: true
    });
    const blob = await exportFiles(
      props.accessToken,
      props.filterQuery,
      "procedures"
    );

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;

    link.setAttribute(
      "download",
      `Procedure History Report @${dateAndTimeConverter(
        Date.now(),
        "Time"
      ).replace(":", "-")},
    ${dateAndTimeConverter(Date.now(), "cardDate").replace(":", "-")}.xlsx`
    );
    document.body.appendChild(link);
    link.click();

    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    props.setProcedureHistoryLoading({
      ...props.procedureHistoryLoading,
      export: false
    });
  } catch (error) {
    console.error("There was an error downloading the file:", error);
  }
};

const getPaginationIncrement = async (props) => {
  if (
    typeof props.procedureHistoryListener !== "function" ||
    props.procedureHistoryData === null ||
    JSON.stringify(props.listeningFilterData) !==
      JSON.stringify(props.filterQuery)
  ) {
    return;
  }

  props.setProcedureHistoryLoading({
    ...props.procedureHistoryLoading,
    pagination: true
  });

  if (!props.currentCursor || !props.pageNumber) {
    return props.setProcedureHistoryLoading({
      ...props.procedureHistoryLoading,
      pagination: false
    });
  }

  const hasInequalityFilter =
    Array.isArray(props.listeningFilterData) &&
    props.listeningFilterData.some((operator) =>
      ["<", "<=", ">", ">=", "not-in", "!="].includes(operator)
    );

  const sortField = procedureHistoryTableField(props.sort?.title, "sort");
  const sortOrder = props.sort?.ascending ? "asc" : "desc";
  const baseQueryConstraints = [
    orderBy(sortField, sortOrder),
    startAfter(props.currentCursor.next),
    limit(listenerDataLimit)
  ];

  let paginationQueryRef;

  if (hasInequalityFilter) {
    paginationQueryRef = !sortField
      ? query(
          collectionGroup(database, "procedureHistory"),
          orderBy("createdAt", "desc"),
          ...baseQueryConstraints
        )
      : query(
          collectionGroup(database, "procedureHistory"),
          ...baseQueryConstraints
        );
  } else {
    paginationQueryRef = query(
      collectionGroup(database, "procedureHistory"),

      ...baseQueryConstraints
    );
  }

  props.listeningFilterData?.forEach((value, index) => {
    if ((index + 1) % 3 === 0 && index !== 0) {
      paginationQueryRef = query(
        paginationQueryRef,
        where(
          props.listeningFilterData[index - 2],
          props.listeningFilterData[index - 1],
          value
        )
      );
    }
  });

  const querySnapshot = await getDocs(paginationQueryRef);
  let procedureHistory = {};
  querySnapshot.forEach((doc) => {
    procedureHistory[doc.id] = {
      ...doc.data(),
      documentId: doc.id
    };
  });
  props.procedureHistoryData = procedureHistory;

  props.setProcedureHistoryLoading({
    ...props.procedureHistoryLoading,
    pagination: false
  });
  putProceduresHistory(props.procedureHistoryData, props.filterQuery);
  return {
    next: querySnapshot.docs[querySnapshot.docs.length - 1],
    prev: querySnapshot.docs[0]
  };
};

const getPaginationDecrement = async (props) => {
  if (
    typeof props.procedureHistoryListener !== "function" ||
    props.procedureHistoryData === null ||
    JSON.stringify(props.listeningFilterData) !==
      JSON.stringify(props.filterQuery)
  ) {
    return;
  }

  props.setProcedureHistoryLoading({
    ...props.procedureHistoryLoading,
    pagination: true
  });

  if (!props.currentCursor || !props.pageNumber) {
    return props.setProcedureHistoryLoading({
      ...props.procedureHistoryLoading,
      pagination: false
    });
  }

  const hasInequalityFilter =
    Array.isArray(props.listeningFilterData) &&
    props.listeningFilterData.some((operator) =>
      ["<", "<=", ">", ">=", "not-in", "!="].includes(operator)
    );

  const sortField = procedureHistoryTableField(props.sort?.title, "sort");
  const sortOrder = props.sort?.ascending ? "asc" : "desc";
  const baseQueryConstraints = [
    orderBy(sortField, sortOrder),
    endBefore(props.currentCursor.prev),
    limitToLast(listenerDataLimit)
  ];

  let paginationQueryRef;

  if (hasInequalityFilter) {
    paginationQueryRef =
      sortField !== "createdAt"
        ? query(
            collectionGroup(database, "procedureHistory"),
            orderBy("createdAt", "desc"),
            ...baseQueryConstraints
          )
        : query(
            collectionGroup(database, "procedureHistory"),
            ...baseQueryConstraints
          );
  } else {
    paginationQueryRef = query(
      collectionGroup(database, "procedureHistory"),
      ...baseQueryConstraints
    );
  }

  props.listeningFilterData?.forEach((value, index) => {
    if ((index + 1) % 3 === 0 && index !== 0) {
      paginationQueryRef = query(
        paginationQueryRef,
        where(
          props.listeningFilterData[index - 2],
          props.listeningFilterData[index - 1],
          value
        )
      );
    }
  });

  const querySnapshot = await getDocs(paginationQueryRef);
  let procedureHistory = {};
  querySnapshot.forEach((doc) => {
    procedureHistory[doc.id] = {
      ...doc.data(),
      documentId: doc.id
    };
  });

  props.procedureHistoryData = procedureHistory;

  props.setProcedureHistoryLoading({
    ...props.procedureHistoryLoading,
    pagination: false
  });
  putProceduresHistory(props.procedureHistoryData, props.filterQuery);
  return {
    next: querySnapshot.docs[querySnapshot.docs.length - 1],
    prev: querySnapshot.docs[0]
  };
};
